import moment from 'moment-timezone';

import type { PendingSites } from '../../../store/reducers/reportsubmit';
import type { ReportIndex, ReportSummary, SiteReport } from '../../../utils/classes';
import type { ScheduleListItemData } from '../../../typing/scheduleItem';

const getScheduledDate = (reportKey: string, summary?: Partial<ReportSummary> | null) => {
  if (summary?.datetimeScheduled) {
    return moment.parseZone(summary.datetimeScheduled).format('YYYY-MM-DD');
  }

  return reportKey.split('_')[0] || '';
};

const matchesScheduleItem = (
  item: ScheduleListItemData,
  reportKey: string,
  summary?: Partial<ReportSummary> | null
) => {
  if (!summary || summary.scheduleKey !== item.scheduleID) {
    return false;
  }

  if (summary.siteKey && summary.siteKey !== item.siteID) {
    return false;
  }

  if (summary.questionnaire && summary.questionnaire !== item.questionnaireID) {
    return false;
  }

  if (typeof summary.isAdhoc === 'boolean' && summary.isAdhoc !== (item.scheduleType === 'adhoc')) {
    return false;
  }

  return getScheduledDate(reportKey, summary) === item.scheduledDate;
};

const sortByLatest = (
  [reportKeyA, summaryA]: [string, ReportSummary],
  [reportKeyB, summaryB]: [string, ReportSummary]
) => {
  const timeA = summaryA.datetimeUpdated || summaryA.datetimeIn || reportKeyA;
  const timeB = summaryB.datetimeUpdated || summaryB.datetimeIn || reportKeyB;

  return moment.parseZone(timeB).valueOf() - moment.parseZone(timeA).valueOf();
};

export const reconcileScheduleItemWithLocalState = (
  item: ScheduleListItemData,
  siteReports: SiteReport | undefined,
  siteSummaries: ReportIndex | undefined,
  pendingSites: PendingSites | undefined
): ScheduleListItemData => {
  const summaries = Object.entries(siteSummaries || {}).filter(([reportKey, summary]) =>
    matchesScheduleItem(item, reportKey, summary)
  );

  const latestSummaryEntry = summaries.sort(sortByLatest)[0];
  const latestReportKey = latestSummaryEntry?.[0];
  const latestSummary = latestSummaryEntry?.[1];
  const hasLocalReport = !!(latestReportKey && siteReports?.[latestReportKey]);
  const pendingReportKey = pendingSites?.[item.siteID]?.pendingSingle?.[item.scheduleID];
  const hasPendingReportData = pendingReportKey ? !!siteReports?.[pendingReportKey] : false;
  const pendingSummary = pendingReportKey ? siteSummaries?.[pendingReportKey] : null;
  const isPendingUpload =
    !!pendingReportKey && hasPendingReportData && matchesScheduleItem(item, pendingReportKey, pendingSummary);

  if (isPendingUpload) {
    return {
      ...item,
      hasDraft: false,
      isPendingUpload: true,
      draftSource: 'none'
    };
  }

  if (!latestSummary) {
    if (item.hasDraft && item.firebaseReportID) {
      return {
        ...item,
        hasDraft: false,
        isPendingUpload: false,
        status: 'in-progress',
        draftSource: 'remote'
      };
    }

    if (item.hasDraft) {
      return {
        ...item,
        hasDraft: false,
        isPendingUpload: false,
        status: 'not-started',
        draftSource: 'none'
      };
    }

    return {
      ...item,
      draftSource: 'none'
    };
  }

  if (latestSummary.status === 'draft' && hasLocalReport) {
    return {
      ...item,
      hasDraft: true,
      isPendingUpload: false,
      status: 'in-progress',
      draftSource: 'local'
    };
  }

  if (latestSummary.status === 'draft') {
    return {
      ...item,
      hasDraft: false,
      isPendingUpload: false,
      status: item.firebaseReportID ? 'in-progress' : 'not-started',
      draftSource: item.firebaseReportID ? 'remote' : 'none'
    };
  }

  if (latestSummary.status === 'complete' || latestSummary.status === 'expired') {
    return {
      ...item,
      hasDraft: false,
      isPendingUpload: false,
      status: 'completed',
      draftSource: 'none'
    };
  }

  return {
    ...item,
    hasDraft: false,
    isPendingUpload: false,
    draftSource: 'none'
  };
};

export const reconcileScheduleItemsWithLocalState = (
  items: ScheduleListItemData[],
  cacheReports: { [siteKey: string]: SiteReport } | undefined,
  cacheSummaries: { [siteKey: string]: ReportIndex } | undefined,
  pendingSites: PendingSites | undefined
): ScheduleListItemData[] => {
  return items.map(item =>
    reconcileScheduleItemWithLocalState(item, cacheReports?.[item.siteID], cacheSummaries?.[item.siteID], pendingSites)
  );
};
